import moment, { type Moment } from "moment";
import { type Customer, type LocalCustomer } from "#/Routes/Customers/CustomerTypes.ts";
import { type Override } from "../../../../types/internal/utility";

/**
 * Convert a customer from IndexedDB to GraphQL
 * @param {LocalCustomer} customer
 * @returns {Customer}
 */
export function convertFromIndexedDBToGraphQL(customer: LocalCustomer): Customer {
    return {
        id                  : customer.uuid,
        firstName           : customer.first_name,
        lastName            : customer.last_name,
        code                : customer.code,
        company             : customer.company,
        businessNumber      : customer.business_number,
        birthday            : customer.birthday as string,
        gender              : customer.gender,
        emails              : typeof customer.email === "string" ? customer.email.split(";") : [],
        allowMarketingEmails: null,
        fax                 : customer.billing.fax,
        phone               : customer.billing.phone,
        mobile              : customer.billing.mobile,
        website             : customer.billing.website,
        twitter             : customer.billing.twitter,
        facebook            : customer.billing.facebook,
        billing             : {
            addressStreet1 : customer.billing.address_street_1,
            addressStreet2 : customer.billing.address_street_2,
            addressSuburb  : customer.billing.address_suburb,
            addressCity    : customer.billing.address_city,
            addressPostcode: customer.billing.address_postcode,
            addressCountry : customer.billing.address_country,
            addressRegion  : customer.billing.address_state,
        },
        delivery: {
            addressStreet1 : customer.delivery.address_street_1,
            addressStreet2 : customer.delivery.address_street_2,
            addressSuburb  : customer.delivery.address_suburb,
            addressCity    : customer.delivery.address_city,
            addressPostcode: customer.delivery.address_postcode,
            addressCountry : customer.delivery.address_country,
            addressRegion  : customer.delivery.address_state,
        },
        invoiceMessage       : customer.invoice_message,
        comments             : customer.comments,
        customerGroup        : customer.customer_group_id,
        loyaltyEnabled       : customer.loyalty,
        loyaltyRate          : customer.loyalty_rate,
        loyaltyPoints        : customer.loyalty_points,
        disablePromotions    : customer.disable_promotions,
        priceList            : customer.pricelist_id,
        accountLimit         : customer.account_limit,
        currentOwing         : customer.current_owing,
        overrideCustomerGroup: customer.customer_group_id === null ? false : customer.override_customer_group,
        requireOrderReference: !!customer.require_order_reference,
    };
}

/**
 * Convert a customer from GraphQL to IndexedDB
 * @param {Customer} customer
 * @returns {LocalCustomer}
 *
 * NOTE: Fields from the customer group are not correctly resolved from this function
 */
export function convertFromGraphQLToIndexedDB(
    customer: Override<Customer, {
        birthday: string | Moment | null;
        accountLimit: string | null;
    }>
): LocalCustomer {
    if(customer.birthday instanceof moment) {
        customer.birthday = (customer.birthday as Moment).format("YYYY-MM-DD");
    }

    return {
        first_name     : customer.firstName,
        last_name      : customer.lastName,
        code           : customer.code,
        company        : customer.company,
        business_number: customer.businessNumber,
        birthday       : customer.birthday as string,
        gender         : customer.gender,
        email          : customer.emails.join(";"),
        billing        : {
            address_street_1: customer.billing.addressStreet1,
            address_street_2: customer.billing.addressStreet2,
            address_suburb  : customer.billing.addressSuburb,
            address_city    : customer.billing.addressCity,
            address_postcode: customer.billing.addressPostcode,
            address_country : customer.billing.addressCountry,
            address_state   : customer.billing.addressState || customer.billing.addressRegion,
            fax             : customer.fax,
            phone           : customer.phone,
            mobile          : customer.mobile,
            website         : customer.website,
            twitter         : customer.twitter,
            facebook        : customer.facebook,
            email           : "",
            uuid            : "",
        },
        delivery: {
            address_street_1: customer.delivery.addressStreet1,
            address_street_2: customer.delivery.addressStreet2,
            address_suburb  : customer.delivery.addressSuburb,
            address_city    : customer.delivery.addressCity,
            address_postcode: customer.delivery.addressPostcode,
            address_country : customer.delivery.addressCountry,
            address_state   : customer.delivery.addressState || customer.delivery.addressRegion,
            fax             : "",
            phone           : "",
            mobile          : "",
            website         : "",
            twitter         : "",
            facebook        : "",
            email           : "",
            uuid            : "",
        },
        invoice_message   : customer.invoiceMessage,
        comments          : customer.comments,
        customer_group_id : customer.customerGroup,
        loyalty           : customer.loyaltyEnabled,
        loyalty_rate      : customer.loyaltyRate,
        loyalty_points    : customer.loyaltyPoints,
        disable_promotions: customer.disablePromotions,
        pricelist_id      : customer.priceList,
        account_limit     : (customer.accountLimit || "").trim() === "" ?
            null :
            parseFloat(customer.accountLimit as string),
        current_owing            : customer.currentOwing,
        override_customer_group  : customer.overrideCustomerGroup,
        require_order_reference  : customer.requireOrderReference,
        // NOTE: The following fields are expected to be overridden outside of this method
        account_receipt          : null,
        allow_account_sales      : false,
        autoprint_account_receipt: false,
        autoprint_receipt        : false,
        autoprint_parked_receipt : false,
        billing_contact_id       : "",
        delivery_contact_id      : "",
        client_id                : "",
        merged_into              : null,
        receipt                  : null,
        uuid                     : customer.id as string,
        full_name                : `${customer.firstName} ${customer.lastName}`,
        phones                   : [ customer.phone, customer.mobile ],
    };
}

export type MarketingEmailRules = "optIn" | "optOut" | "notSpecified";

export const marketingEmailRulesMap = new Map<MarketingEmailRules, boolean | null>([
    [ "notSpecified", null ],
    [ "optIn", true ],
    [ "optOut", false ],
]);

export const marketingEmailRulesReverseMap = new Map<boolean | null, MarketingEmailRules>(
    Array.from(marketingEmailRulesMap.entries()).map(([ key, value ]) => [ value, key ])
);
